import React from "react";
import { ComponentHelper } from "@magnolia/react-editor";

/**
 * Renders a Component Factory item that a page embeds, the headless counterpart of the
 * wrapper component's FreeMarker script.
 *
 * The page itself stores only the item's uuid in `component-factory-link`. FreeMarker follows
 * it at render time with cmsfn.contentById; a frontend receiving one JSON document cannot, so
 * the delivery endpoint resolves it (see the endpoint decoration in the
 * feature-component-factory light module). The v2 delivery endpoint resolves IN PLACE: the
 * `component-factory-link` prop itself is the resolved node, verified against the live
 * response. When the resolver is missing or may not read the factory workspace, the prop
 * stays the raw uuid string, and this renders nothing rather than breaking the page.
 *
 * The embedded components are rendered WITHOUT edit annotations, matching the FreeMarker
 * version's `editable=false`: they belong to a different page, so editing them here would
 * write to the wrong node. The factory app is where they are edited, and the overlay below
 * says so instead of silently swallowing clicks.
 */
const ComponentFactoryLink = (props) => {
    // The JCR property is component-factory-link, but the react-editor renames dashed keys
    // when building props: buildComponentProperties does a.replace("-", "_") with a STRING
    // argument, which only replaces the FIRST dash, so the prop arrives as
    // "component_factory-link". All three spellings are read so this survives a future
    // replaceAll fix in the library as well. A string value means "unresolved uuid".
    const raw =
        props["component_factory-link"] ?? props["component_factory_link"] ?? props["component-factory-link"];
    const linked = raw && typeof raw === "object" ? raw : null;

    // A wrapper that overrides values of the item for this page also carries
    // `component-factory-overrides`. The endpoint's componentFactoryOverridesResolver turns that
    // JSON into the same item WITH the overrides applied, so it is preferred over the raw item
    // whenever both resolve to the same node (stale overrides for a re-linked item are ignored).
    const mergedRaw =
        props["component_factory-overrides"] ?? props["component_factory_overrides"] ?? props["component-factory-overrides"];
    const merged = mergedRaw && typeof mergedRaw === "object" ? mergedRaw : null;
    const item = merged && (!linked || merged["@id"] === linked["@id"]) ? merged : linked;

    const mappings = ComponentHelper.getConfigRef()?.componentMappings;
    const isEditMode = Boolean(ComponentHelper.getMagnoliaContextRef()?.isMagnoliaEdit);

    // The reference resolves to the item page; its components sit in the `main` area. `@nodes`
    // is the delivery API's ordered list of child node names, so it also carries the order the
    // editor shows.
    const main = item?.main;
    const componentNames = main?.["@nodes"] ?? [];

    if (!item) return null;

    if (!componentNames.length) {
        return isEditMode ? (
            <div className="component-factory-component-empty">
                <p>The linked component has no components in its main area.</p>
            </div>
        ) : null;
    }

    return (
        <div className="component-factory-component" data-component-factory="true" style={{ position: "relative" }}>
            {isEditMode && (
                <div
                    style={{
                        position: "absolute",
                        inset: 0,
                        zIndex: 9999,
                        cursor: "default",
                        background: "rgba(255, 255, 255, 0.01)",
                    }}
                    title="Components from the Component Factory are not editable here. Edit them in the Component Factory app."
                />
            )}
            {componentNames.map((name) => {
                const component = main[name];

                return (
                    <div
                        key={name}
                        className="component-factory-item"
                        data-component-template={component?.["mgnl:template"]}
                    >
                        {ComponentHelper.getRenderedComponent(component, mappings)}
                    </div>
                );
            })}
        </div>
    );
};

export default ComponentFactoryLink;
